"use client";

import Button from "@/components/Button";
import CTAGroup from "@/components/CTAGroup";
import AddressSuggestionButton from "@/components/CallHeavyHero/sub-components/AddressSuggestionButton";
import IconCaretListRight from "@/components/icons/IconCaretListRight";
import IconLocation from "@/components/icons/IconLocation";
import { SERVICEABILITY_ADDRESS_NR_MASK_PROPS } from "@/constants/newRelicSessionReplay";
import { useAppContext } from "@/context/AppContext";
import clientSide from "@/helpers/clientSide";
import { useServiceability } from "@/hooks/useServiceability";
import cn from "@/lib/cn";
import fireElementClicked from "@/lib/tagular/fire-element-clicked";
import fireFieldInputted from "@/lib/tagular/fire-field-inputted";
import type { ServiceabilityAddressSuggestion } from "@/types/serviceability";
import type { TAGULAR_ELEMENT } from "@/types/tagular";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import type { LocalizationServiceabilityProps } from "./types";

const DEFAULT_STATUS_COPY = {
  searching: "Searching addresses...",
  empty: "No address matches yet.",
  selectedAddressLabel: "Selected address",
};

const DEFAULT_PRIMARY_CTA_COPY = "Check availability";
const DEFAULT_PRIMARY_CTA_LOADING_COPY = "Checking availability...";
const DEFAULT_FIELD_NAME = "serviceabilityAddress";

type SearchStatus = ReturnType<typeof useServiceability>["searchStatus"];
type SelectedAddress = ReturnType<typeof useServiceability>["selectedAddress"];

const renderSearchStatus = (
  searchStatus: SearchStatus,
  searchingCopy: string,
  emptyCopy: string
): React.ReactNode => {
  if (searchStatus === "searching") {
    return <p className="text-sm text-neutral-600">{searchingCopy}</p>;
  }

  if (searchStatus === "empty") {
    return <p className="text-sm text-neutral-600">{emptyCopy}</p>;
  }

  return null;
};

const SelectedAddressBlock: React.FC<{
  selectedAddress: SelectedAddress;
  showSelectedAddress: boolean;
  selectedAddressLabel: string;
}> = ({ selectedAddress, showSelectedAddress, selectedAddressLabel }) => (
  <>
    {showSelectedAddress && selectedAddress && (
      <div className="rounded-md border border-neutral-300 bg-white p-4">
        <p className="text-sm font-medium text-neutral-900">
          {selectedAddressLabel}
        </p>
        <p
          className="mt-1 text-sm text-neutral-700"
          {...SERVICEABILITY_ADDRESS_NR_MASK_PROPS}
        >
          {selectedAddress.fullAddress}
        </p>
      </div>
    )}
  </>
);

const LocalizationServiceability: React.FC<LocalizationServiceabilityProps> = ({
  field,
  primaryCta,
  secondaryCtaGroup,
  statusCopy,
  showSelectedAddress = true,
  showLocationIcon = true,
  mobileCtaDisplay = "text",
  statusPlacement = "inline",
  ctaPlacement = "below",
  secondaryCtaTabletPlacement = "inline",
  inputContainerClassName,
  rowClassName,
  inputWrapperClassName,
  inputClassName,
  ctaContainerClassName,
  statusWrapperClassName,
  dropdownClassName,
  submitTracking,
  inputEngagedTracking,
  suggestionsTestId,
  destinationUrl,
}) => {
  const {
    inputValue,
    suggestions,
    selectedAddress,
    searchStatus,
    submitStatus,
    nextUrl,
    setInputValue,
    selectAddress,
    submitSelection,
    canSubmit,
  } = useServiceability(destinationUrl);
  const { localizationType } = useAppContext();
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const inputRef = useRef<HTMLInputElement>(null);

  const resolvedFieldName = field.fieldName ?? DEFAULT_FIELD_NAME;
  const searchingCopy = statusCopy?.searching ?? DEFAULT_STATUS_COPY.searching;
  const emptyCopy = statusCopy?.empty ?? DEFAULT_STATUS_COPY.empty;
  const selectedAddressLabel =
    statusCopy?.selectedAddressLabel ??
    DEFAULT_STATUS_COPY.selectedAddressLabel;
  const primaryCtaCopy = primaryCta?.copy?.trim() || DEFAULT_PRIMARY_CTA_COPY;
  const primaryCtaLoadingCopy =
    primaryCta?.loadingCopy?.trim() || DEFAULT_PRIMARY_CTA_LOADING_COPY;

  const selectedAddressContent = useMemo(
    () => (
      <SelectedAddressBlock
        selectedAddress={selectedAddress}
        showSelectedAddress={showSelectedAddress}
        selectedAddressLabel={selectedAddressLabel}
      />
    ),
    [selectedAddress, selectedAddressLabel, showSelectedAddress]
  );
  const statusContent = useMemo(
    () => renderSearchStatus(searchStatus, searchingCopy, emptyCopy),
    [emptyCopy, searchStatus, searchingCopy]
  );
  const isInlineCtaPlacement = ctaPlacement === "inline";
  const useIconMobileCta = mobileCtaDisplay === "icon";
  const shouldRenderSecondaryCtaBelowRowOnTablet =
    secondaryCtaTabletPlacement === "below";
  const secondaryCtaContent = secondaryCtaGroup?.ctas?.length ? (
    <div className="self-center">
      <CTAGroup
        ctas={secondaryCtaGroup.ctas}
        className={secondaryCtaGroup.className}
        buttonClassName={secondaryCtaGroup.buttonClassName}
        defaultDisplay={secondaryCtaGroup.defaultDisplay}
        defaultTone={secondaryCtaGroup.defaultTone}
        defaultSize={secondaryCtaGroup.defaultSize}
        orientation={secondaryCtaGroup.orientation}
        alignment={secondaryCtaGroup.alignment}
        wrap={secondaryCtaGroup.wrap}
        location={secondaryCtaGroup.location}
        elementName={secondaryCtaGroup.elementName}
        tagularProps={secondaryCtaGroup.tagularProps}
        localizationType={secondaryCtaGroup.localizationType}
        pricingView={secondaryCtaGroup.pricingView}
        onModalOpen={secondaryCtaGroup.onModalOpen}
      />
    </div>
  ) : null;

  useEffect(() => {
    if (!clientSide) {
      return;
    }

    const updateDropdownPosition = () => {
      if (!inputRef.current || suggestions.length === 0) {
        return;
      }

      const rect = inputRef.current.getBoundingClientRect();

      setDropdownStyle({
        position: "fixed",
        top: `${rect.bottom + 8}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        zIndex: 60,
      });
    };

    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);

    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
    };
  }, [suggestions.length]);

  useEffect(() => {
    if (!nextUrl) {
      return;
    }

    window.location.href = nextUrl;
  }, [nextUrl]);

  const handleAddressFieldEngaged = () => {
    if (!inputEngagedTracking) {
      return;
    }

    fireElementClicked(inputEngagedTracking as TAGULAR_ELEMENT);
  };

  const handleAddressInputBlur = () => {
    if (!inputValue.trim()) {
      return;
    }

    fireFieldInputted({
      autofilled: false,
      fieldId: field.inputId,
      fieldLabel:
        field.blurFieldLabel || field.label || field.placeholder || "",
      fieldName: resolvedFieldName,
      fieldType: "text",
      fieldValue: "",
    });
  };

  const handleAddressSelect = (address: ServiceabilityAddressSuggestion) => {
    selectAddress(address, {
      autofilled: true,
      fieldId: field.inputId,
      fieldLabel:
        field.selectFieldLabel || field.label || field.placeholder || "",
      fieldName: resolvedFieldName,
      fieldType: "dropdown",
      fieldValue: "",
    });
  };

  const handleSubmitClick = () => {
    if (!selectedAddress) {
      return;
    }

    void submitSelection();

    if (!submitTracking) {
      return;
    }

    fireElementClicked({
      actionOutcome:
        localizationType === "full"
          ? submitTracking.actionOutcomeWhenLocalized
          : submitTracking.actionOutcomeWhenNotLocalized,
      elementType: submitTracking.elementType,
      location: submitTracking.location,
      text: submitTracking.text ?? primaryCtaCopy,
      name: submitTracking.name,
      ...(submitTracking.monarchMetadata && {
        monarchMetadata: submitTracking.monarchMetadata,
      }),
    } as TAGULAR_ELEMENT);
  };

  const handleAddressKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    handleSubmitClick();
  };

  const suggestionsDropdown =
    clientSide && suggestions.length > 0
      ? createPortal(
          <div
            data-testid={suggestionsTestId}
            style={dropdownStyle}
            className={cn(
              "max-h-100 overflow-y-auto rounded-md border border-neutral-300 bg-white shadow-lg",
              dropdownClassName
            )}
          >
            {suggestions.map((suggestion) => (
              <AddressSuggestionButton
                key={`${suggestion.fullAddress}-${suggestion.locationId ?? suggestion.geofabricId ?? "missing-id"}`}
                address={suggestion}
                onSelect={handleAddressSelect}
              />
            ))}
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <div className={cn("flex flex-col gap-2", inputContainerClassName)}>
        <div
          className={cn(
            "flex flex-col gap-2",
            isInlineCtaPlacement && "md:flex-row md:items-end md:gap-4",
            rowClassName
          )}
        >
          <div
            className={cn(
              "flex flex-col gap-2",
              isInlineCtaPlacement && "flex-1"
            )}
          >
            <div className={cn("relative block", inputWrapperClassName)}>
              {field.label && (
                <label
                  className={cn(
                    "mb-1 block text-sm text-neutral-900",
                    field.labelClassName
                  )}
                  htmlFor={field.inputId}
                >
                  {field.label}
                </label>
              )}

              <div className="relative">
                {showLocationIcon && (
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                    <IconLocation
                      theme="black"
                      style="outline"
                      className="h-5 w-5"
                    />
                  </div>
                )}

                <input
                  ref={inputRef}
                  id={field.inputId}
                  type="text"
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                  onFocus={handleAddressFieldEngaged}
                  onBlur={handleAddressInputBlur}
                  onKeyDown={handleAddressKeyDown}
                  placeholder={field.placeholder}
                  aria-label={field.ariaLabel}
                  autoComplete={field.autoComplete}
                  className={cn(
                    "w-full rounded-md border border-neutral-400 bg-white pr-4 py-4 text-base text-neutral-900 outline-none transition-colors focus:border-neutral-900",
                    showLocationIcon ? "pl-12" : "pl-4",
                    inputClassName
                  )}
                />
              </div>

              {selectedAddressContent}
            </div>

            {statusPlacement === "inline" && (
              <div className={cn(statusWrapperClassName)}>{statusContent}</div>
            )}
          </div>

          <div
            className={cn(
              "flex flex-col gap-5 md:gap-6 md:flex-row",
              isInlineCtaPlacement && "md:shrink-0 md:self-end",
              ctaContainerClassName
            )}
          >
            <Button
              onClick={handleSubmitClick}
              disabled={!canSubmit}
              display={primaryCta?.display ?? "fill"}
              tone={primaryCta?.tone ?? "neutral-dark"}
              size={primaryCta?.size ?? "md"}
              className={cn(
                "w-full sm:w-auto",
                useIconMobileCta && "hidden md:inline-flex",
                primaryCta?.className
              )}
            >
              {submitStatus === "submitting"
                ? primaryCtaLoadingCopy
                : primaryCtaCopy}
            </Button>

            {useIconMobileCta && (
              <Button
                onClick={handleSubmitClick}
                disabled={!canSubmit}
                display={primaryCta?.display ?? "fill"}
                tone={primaryCta?.tone ?? "neutral-dark"}
                size={primaryCta?.size ?? "md"}
                className="inline-flex h-14 w-14 shrink-0 p-0 md:hidden"
                aria-label={primaryCtaCopy}
              >
                <IconCaretListRight theme="white" className="h-8 w-8" />
              </Button>
            )}

            {!shouldRenderSecondaryCtaBelowRowOnTablet && secondaryCtaContent}
            {shouldRenderSecondaryCtaBelowRowOnTablet && (
              <div className="md:hidden xl:block">{secondaryCtaContent}</div>
            )}
          </div>
        </div>

        {shouldRenderSecondaryCtaBelowRowOnTablet && (
          <div className="hidden md:block xl:hidden">{secondaryCtaContent}</div>
        )}

        {statusPlacement === "below" && (
          <div className={cn("flex flex-col gap-2", statusWrapperClassName)}>
            {statusContent}
          </div>
        )}
      </div>

      {suggestionsDropdown}
    </>
  );
};

export default LocalizationServiceability;
